import React, { useState, useEffect, useCallback, useRef } from 'react';
import pt from 'prop-types';
import cx from 'classnames';

import useOverlayPosition from 'components/storefront/editor/easyblocks-page/components/overlay/use-overlay-position';
import {
  MESSAGE_EVENTS,
  postEasyblocksFocus,
  postStorefrontItemHover,
  postStorefrontItemUnhover,
} from 'components/storefront/editor/utils/post-message';

import BlockLabel from './label';
import AddBlockButtons from './add-block-buttons';

import './block-overlay.scss';

function BlockOverlay({
  path,
  selected,
  sectionSelected,
  sectionHovered,
  blockData,
  blockConfigs,
  element,
}) {
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const overlayRef = useRef(null);

  useOverlayPosition({ current: element }, overlayRef);

  useEffect(() => {
    const eventListener = (event) => {
      const { type, payload } = event.data;

      switch (type) {
        case MESSAGE_EVENTS.STOREFRONT_EDITOR.ITEM.HOVER:
          if (payload.path === path) {
            setHovered(true);
            setSidebarHovered(true);
          }

          break;

        case MESSAGE_EVENTS.STOREFRONT_EDITOR.ITEM.UNHOVER:
          if (payload.path === path) {
            setHovered(false);
            setSidebarHovered(false);
          }

          break;

        default:
          break;
      }
    };

    window.addEventListener('message', eventListener);

    return () => {
      window.removeEventListener('message', eventListener);
    };
  }, [path]);

  const onOpenBlockModal = useCallback(() => {
    setShowBlockModal(true);
  }, []);

  const onCloseBlockModal = useCallback(() => {
    setShowBlockModal(false);
    postStorefrontItemUnhover(path, window.parent);
    setHovered(false);
  }, [path]);

  const onMouseEnter = useCallback(() => {
    postStorefrontItemHover(path, window.parent);
    setHovered(true);
  }, [path]);

  const onMouseLeave = useCallback(() => {
    if (showBlockModal) {
      return;
    }

    postStorefrontItemUnhover(path, window.parent);
    setHovered(false);
  }, [path, showBlockModal]);

  const onClick = useCallback(
    (event) => {
      event.stopPropagation();

      // do not select already selected block
      if (selected) {
        return;
      }

      postEasyblocksFocus(path, window.parent);
    },
    [path, selected],
  );

  useEffect(() => {
    element.addEventListener('mouseenter', onMouseEnter);
    element.addEventListener('mouseleave', onMouseLeave);
    element.addEventListener('click', onClick);

    return () => {
      element.removeEventListener('mouseenter', onMouseEnter);
      element.removeEventListener('mouseleave', onMouseLeave);
      element.removeEventListener('click', onClick);
    };
  }, [element, onMouseEnter, onMouseLeave, onClick]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      ref={overlayRef}
      className={cx('block-overlay', {
        selected,
        'section-selected': sectionSelected,
        hovered,
        'section-hovered': sectionHovered,
        'sidebar-hovered': sidebarHovered,
        'with-buttons': showBlockModal,
      })}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <BlockLabel label={blockData.schema.label} />
      {!selected && (
        <AddBlockButtons
          path={path}
          blockConfigs={blockConfigs}
          onOpenBlockModal={onOpenBlockModal}
          onCloseBlockModal={onCloseBlockModal}
        />
      )}
    </div>
  );
}

BlockOverlay.propTypes = {
  path: pt.string,
  selected: pt.bool,
  sectionSelected: pt.bool,
  sectionHovered: pt.bool,
  blockData: pt.object,
  blockConfigs: pt.object,
  element: pt.instanceOf(HTMLElement),
};

export default React.memo(BlockOverlay);
